import "./Drawer.scss";

import cx from "classnames";
import delay from "lodash/delay";
import React, { useCallback, useMemo } from "react";
import { useFormContext } from "react-hook-form";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { setFocusedField } from "reducers/main";
import { setShowDocumentsDrawer } from "reducers/drawer";
import type { RootState } from "services/store";
import type { ValidationDataType } from "types/DataTypes";
import { useDispatch, useSelector } from "utils/redux";
import { useConstants } from "utils/hooks";

type DrawerProps = {
  visible: boolean;
  type?: "error" | "warning";
};

const Drawer = ({ visible, type = "error" }: DrawerProps) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const { constants } = useConstants();
  const {
    formState: { errors: formErrors },
  } = useFormContext();

  const validationErrors = useSelector((state) => state.main.validationErrors);
  const validationWarnings = useSelector(
    (state) => state.main.validationWarnings,
  );
  const fileId = useSelector((state: RootState) => state.files.fileId);

  const { claimId, msaId, costProjectionId, reviewId, subpage } = useParams<{
    claimId?: string;
    msaId?: string;
    costProjectionId?: string;
    reviewId?: string;
    subpage?: string;
  }>();

  const handleSetFocusedField = useCallback(
    (field: string | null) => {
      // only set red border if type is error
      if (type === "error") {
        dispatch(setFocusedField(field));
        try {
          const input = document.querySelector(
            `[name="${field}"]`,
          ) as HTMLInputElement;
          if (input) {
            input.focus();
          }
        } catch (error) {
          console.error("Error focusing field:", error);
        }
      }
    },
    [dispatch, setFocusedField],
  );

  const onHandleErrorClick = useCallback(
    async (error: ValidationDataType) => {
      delay(() => {
        handleSetFocusedField(error.field);
      }, 250);

      // don't navigate if we're already on the correct page
      if (error.affects === subpage) {
        return;
      }

      // check if this is a document-related warning by looking at field names
      // document warnings use document type constants as field names
      const documentTypes = constants?.DOCUMENT_TYPES || [];
      const documentTypeValues = documentTypes
        .map((dt) => dt.value)
        .filter(Boolean);
      const isDocumentWarning =
        error.field && documentTypeValues.includes(error.field);

      // handle navigation based on the current route
      if (location.pathname.includes("/future-medical/")) {
        // For future medical pages - detect service type from URL
        if (location.pathname.includes("/amended-review/") && msaId) {
          await navigate(
            `/future-medical/amended-review/${msaId}/${error.affects}`,
          );
          if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
        } else if (location.pathname.includes("/ar-screening/") && reviewId) {
          await navigate(
            `/future-medical/ar-screening/${reviewId}/${error.affects}`,
          );
          if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
        } else if (location.pathname.includes("/lcp/") && costProjectionId) {
          await navigate(
            `/future-medical/lcp/${costProjectionId}/${error.affects}`,
          );
          if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
        } else if (location.pathname.includes("/mcp/") && costProjectionId) {
          await navigate(
            `/future-medical/mcp/${costProjectionId}/${error.affects}`,
          );
          if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
        } else if (location.pathname.includes("/msa/") && msaId) {
          await navigate(`/future-medical/msa/${msaId}/${error.affects}`);
          if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
        }
      } else if (location.pathname.includes("/liens/")) {
        const matches = location.pathname.match(/\/liens\/(\d+)\/lien\/(\d+)/);
        if (matches) {
          const [, lienFileId, lienId] = matches;
          // special case for invoice which has a different URL format
          if (error.affects === "invoice") {
            await navigate(`/liens/${lienFileId}/invoice?file_id=${fileId}`);
          } else {
            await navigate(
              `/liens/${lienFileId}/lien/${lienId}/${error.affects}`,
            );
            if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
          }
        }
      } else if (claimId) {
        // for claims pages
        await navigate(`/section-111/claims/${claimId}/${error.affects}`);
        if (isDocumentWarning) dispatch(setShowDocumentsDrawer(true));
      }

      // clear the focused field after navigation and a brief delay
      delay(() => {
        handleSetFocusedField(null);
      }, 1000);
    },
    [
      claimId,
      msaId,
      costProjectionId,
      reviewId,
      subpage,
      location.pathname,
      navigate,
      handleSetFocusedField,
      fileId,
    ],
  );

  const validationErrorContent = useMemo(() => {
    const items = type === "error" ? validationErrors : validationWarnings;

    return (
      <>
        <div className={cx("data-block-header flex ai-c jc-sb", type)}>
          <div>
            You have <span> {items?.length}</span>{" "}
            {type === "error" ? "Actions" : "Warnings"}
          </div>
        </div>

        <div className="data-values">
          {items?.map((error: ValidationDataType, index: number) => {
            const label = error.message.split(":")[0];
            const value = error.message.split(":")[1];

            return (
              <div
                key={`${error.field}-${index}`}
                className="data-value"
                onClick={() => {
                  onHandleErrorClick(error);
                }}
                onMouseOver={() => {
                  handleSetFocusedField(error.field);
                }}
                onMouseOut={() => {
                  handleSetFocusedField(null);
                }}
              >
                <div className="data-value__label">{label}:</div>
                <div className="data-values__item">{value}</div>
              </div>
            );
          })}
        </div>
      </>
    );
  }, [
    type,
    validationErrors,
    validationWarnings,
    onHandleErrorClick,
    handleSetFocusedField,
  ]);

  const formErrorContent = useMemo(() => {
    return (
      <>
        <div className="data-block-header flex ai-c jc-sb error">
          <div>
            You have{" "}
            <span>
              {Object.keys(formErrors).length > 0
                ? Object.keys(formErrors).length - 1
                : 0}
            </span>{" "}
            Errors
          </div>
        </div>

        <div className="data-values">
          {Object.keys(formErrors).map((key, index) => {
            return (
              <React.Fragment key={index}>
                <label>{key}:</label>
                <div className="data-values__item">
                  {formErrors[key]?.message?.toString()}
                </div>
              </React.Fragment>
            );
          })}
        </div>
      </>
    );
  }, [formErrors]);

  return (
    <div className={cx("drawer errors flex jc-se", { open: visible })}>
      <div className="grid-layout gl-1 modal-grid-wrapper w-200">
        <div className="data-block no-hover ">
          {(
            type === "error"
              ? validationErrors?.length
              : validationWarnings?.length
          )
            ? validationErrorContent
            : formErrorContent}
        </div>
      </div>
    </div>
  );
};

Drawer.displayName = "Drawer";

export default Drawer;
